import * as Yup from "yup";

// Initial values of a new security, and the base of an existing one's prefill.
// Every key the server can return a field error for must be here: Formik marks only
// the keys of `values` touched on submit, and a field error is shown only when touched.
export const FUND_DEFAULT_VALUES = {
  id: null,
  name: "",
  tax_treatment: "investment",
  // Additional data for reporting (Tableau). Shown only when the account's
  // show_security_dim_fields is on; the defaults match the server's.
  investment_location: "",
  book_entry_account: "",
  short_name: "",
  exit_method: "",
  end_date_override: "",
  vintage_year: null,
  commitment_given: null,
  is_core: false,
  in_trading_book: true,
};

// Maximum lengths of the free-text reporting fields (server model limits).
export const FUND_DIM_TEXT_MAX_LENGTH = {
  investment_location: 128,
  short_name: 128,
  book_entry_account: 64,
  exit_method: 255,
};

// Types for which the vintage year is relevant.
export const FUND_VINTAGE_TYPES = ["private_investment_fund", "private_investment_fund_unlisted"];

const isBlank = (v) =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && v.trim() === "") ||
  (typeof v === "number" && Number.isNaN(v));

// The vintage year field is shown for fund types, and always when a year is stored,
// so that changing the type does not hide a saved value.
export const showVintageYear = (values) =>
  FUND_VINTAGE_TYPES.includes(values?.type) || !isBlank(values?.vintage_year);

// "2 500,50" -> "2500.50"; numbers are returned as they are.
const normalizeAmount = (v) => {
  if (isBlank(v)) return null;
  if (typeof v === "number") return v;
  return String(v).replace(/\s/g, "").replace(",", ".");
};

const normalizeYear = (v) => {
  if (isBlank(v)) return null;
  return parseInt(String(v).trim(), 10);
};

const isValidYear = (v) => {
  if (isBlank(v)) return true;
  const s = String(v).trim();
  if (!/^\d{4}$/.test(s)) return false;
  const n = parseInt(s, 10);
  return n >= 1900 && n <= 2100;
};

const isValidAmount = (v) => {
  const a = normalizeAmount(v);
  if (a === null) return true;
  const s = typeof a === "number" ? String(a) : a;
  return /^\d+(\.\d{1,2})?$/.test(s);
};

// Form validation. The symbol is required when a security is created; an existing one
// may have none (the server allows it), and its other fields, such as the tax treatment
// (#1571), must stay editable. `t` is optional: without it the Finnish texts are used.
// "Commitment given (EUR)" when the fund has a currency, plain "Commitment given" otherwise,
// so a new fund without a currency does not show empty brackets.
export const labelWithCurrency = (label, currency) =>
  currency ? `${label} (${currency})` : label;

export const buildFundValidationSchema = (required, t) => {
  const tr = (key, fallback) => (t ? t(key, fallback) : fallback);
  const tooLong = tr("PrivateInvestmentFundEdit_text_too_long", "Teksti on liian pitkä");
  const textField = (max) => Yup.string().nullable().max(max, tooLong);
  return Yup.object().shape({
    name: Yup.string().required(required).nullable(),
    type: Yup.string().required(required).nullable(),
    symbol: Yup.string().nullable().when("id", {
      is: (id) => !id,
      then: (schema) => schema.required(required),
    }),
    investment_location: textField(FUND_DIM_TEXT_MAX_LENGTH.investment_location),
    short_name: textField(FUND_DIM_TEXT_MAX_LENGTH.short_name),
    book_entry_account: textField(FUND_DIM_TEXT_MAX_LENGTH.book_entry_account),
    exit_method: textField(FUND_DIM_TEXT_MAX_LENGTH.exit_method),
    vintage_year: Yup.mixed()
      .nullable()
      .test(
        "vintage_year",
        tr("PrivateInvestmentFundEdit_vintage_year_invalid", "Anna vuosi väliltä 1900–2100"),
        isValidYear
      ),
    commitment_given: Yup.mixed()
      .nullable()
      .test(
        "commitment_given",
        tr(
          "PrivateInvestmentFundEdit_commitment_invalid",
          "Anna summa, joka on vähintään 0 ja jossa on enintään 2 desimaalia"
        ),
        isValidAmount
      ),
  });
};

// Request body of PrivateInvestmentFundEdit's save (create and update).
// tax_treatment (#1571) is sent as the form holds it: "investment" for a new security,
// the server's value for an existing one unless the user changed it.
// Empty number fields are sent as null: the server rejects "" for them.
export const buildFundPayload = (values, { account, selectedSector } = {}) => {
  const size = values?.size ? parseFloat(values?.size.toString().replace(",", ".")) : 0;
  return {
    ...values,
    account: account?.id,
    size: size,
    general_sector: selectedSector?.sector_select?.id,
    private_sector: null,
    public_sector: null,
    tag: values?.tag?.id,
    tax_treatment: values?.tax_treatment || "investment",
    vintage_year: normalizeYear(values?.vintage_year),
    commitment_given: normalizeAmount(values?.commitment_given),
    is_core: Boolean(values?.is_core),
    in_trading_book: values?.in_trading_book !== false,
  };
};

// Fields whose server-side error is shown next to the field.
export const FUND_ERROR_FIELDS = [
  "isin",
  "symbol",
  "stock_market",
  "name",
  "size",
  "currency_label",
  "type",
  "sector",
  "tag",
  "tax_treatment",
  "investment_location",
  "book_entry_account",
  "short_name",
  "exit_method",
  "end_date_override",
  "vintage_year",
  "commitment_given",
  "is_core",
  "in_trading_book",
];

export const buildFundErrors = (errorData, t) =>
  FUND_ERROR_FIELDS.reduce((acc, f) => {
    acc[f] = errorData?.[f] ? t(errorData[f]) : null;
    return acc;
  }, {});
